import { LinkedinLogo } from "@phosphor-icons/react";
import { useLocation } from "react-router-dom";
import { C } from "@/brand/tokens";
import { usePageMeta } from "@/lib/usePageMeta";
import { SiteHeader6 } from "@/components/layout/SiteHeader6";
import { SiteFooter6 } from "@/components/layout/SiteFooter6";
import { InteriorHero } from "@/components/common/InteriorHero";
import { FounderPortrait } from "@/components/brand/FounderPortrait";
import { Box6 } from "@/components/common/Box6";
import { Eyebrow6 } from "@/components/common/Eyebrow6";
import { Cta6 } from "@/components/common/Cta6";
import { CtaBand6 } from "@/components/common/CtaBand6";
import { Pill6 } from "@/components/common/Pill6";
import { aboutFirm6, aboutKatrina6, brand6 } from "@/brand/contentF6";

function BoxLabel({ children, dark }) {
  return (
    <p
      className="font-inter text-[10.5px] uppercase font-semibold pb-3 mb-5"
      style={{ color: C.accentMetal, letterSpacing: "0.26em", borderBottom: `1px solid ${dark ? C.brassSoft : C.brassSoft}` }}
    >
      {children}
    </p>
  );
}

function SectionHead({ eyebrow, headline, intro, dark }) {
  return (
    <div className="grid lg:grid-cols-12 gap-10 mb-12">
      <div className="lg:col-span-6">
        <Eyebrow6 className="mb-6">{eyebrow}</Eyebrow6>
        <h2
          className="font-cormorant"
          style={{ color: dark ? C.textOnDark : C.textOnLight, fontSize: "clamp(1.9rem, 3.4vw, 2.8rem)", fontWeight: 400, lineHeight: 1.08, letterSpacing: "-0.012em", maxWidth: "20ch" }}
        >
          {headline}
        </h2>
      </div>
      {intro && (
        <div className="lg:col-span-5 lg:col-start-8 lg:pt-3">
          <p className="font-inter leading-[1.7] max-w-[48ch]" style={{ color: dark ? C.whiteOnDark : C.inkOnLight, fontSize: "1rem" }}>
            {intro}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * AboutKatrinaPage — dual-purpose About page.
 * 1. About Faith 1st Consulting (firm)  2. About the Founder (#founder)
 * 3. Speaking & Keynotes (#speaking)     4. Closing CTA
 */
export default function AboutKatrinaPage() {
  const sp = aboutKatrina6.speaking;
  const { hash } = useLocation();
  const meta =
    hash === "#speaking"
      ? { title: "Speaking & Keynotes", description: sp.intro }
      : hash === "#founder"
        ? { title: `About the Founder — ${aboutKatrina6.title}`, description: aboutKatrina6.lede }
        : { title: "About", description: aboutFirm6.lede };
  usePageMeta(meta);

  return (
    <main data-testid="about-katrina-page" style={{ background: C.surfaceLight, color: C.textOnLight }}>
      <SiteHeader6 />

      <InteriorHero
        breadcrumb={[{ label: "Faith 1st Consulting", href: "/" }, { label: "About" }]}
        eyebrow={aboutFirm6.eyebrow}
        title={aboutFirm6.title}
        intro={aboutFirm6.lede}
        meta={aboutFirm6.meta}
      />

      {/* 1 — The firm */}
      <section id="firm" data-testid="about-firm" style={{ background: C.surfaceLight }}>
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-5 mb-5">
            <Box6 tone="parchment" radius="card" className="p-9" data-testid="about-what">
              <BoxLabel>{aboutFirm6.what.label}</BoxLabel>
              <p className="font-inter leading-[1.75]" style={{ color: C.inkOnLight, fontSize: "1rem" }}>
                {aboutFirm6.what.body}
              </p>
            </Box6>
            <Box6 tone="parchment" radius="card" className="p-9" data-testid="about-who">
              <BoxLabel>{aboutFirm6.who.label}</BoxLabel>
              <p className="font-inter leading-[1.75]" style={{ color: C.inkOnLight, fontSize: "1rem" }}>
                {aboutFirm6.who.body}
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-8">
                {aboutFirm6.who.list.map((item) => (
                  <li key={item} className="font-inter text-[13.5px] py-2.5" style={{ color: C.textOnLight, borderBottom: `1px solid ${C.paperOver}` }}>
                    {item}
                  </li>
                ))}
              </ul>
            </Box6>
          </div>

          <div className="mt-16">
            <SectionHead eyebrow={aboutFirm6.principlesEyebrow} headline={aboutFirm6.principlesHeadline} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="about-principles">
              {aboutFirm6.principles.map((p, i) => (
                <Box6 key={p.label} tone="white" radius="resource" className="p-7" data-testid={`principle-${i}`}>
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-cormorant tabular-nums" style={{ color: C.accentMetal, fontSize: "1.3rem", lineHeight: 1 }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-cormorant" style={{ color: C.textOnLight, fontSize: "1.25rem", fontWeight: 400, lineHeight: 1.2 }}>
                      {p.label}
                    </h3>
                  </div>
                  <p className="font-inter leading-[1.65]" style={{ color: C.inkOnLight, fontSize: "0.93rem" }}>
                    {p.body}
                  </p>
                </Box6>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2 — The founder */}
      <section id="founder" data-testid="about-founder" style={{ background: C.surfaceLight2, borderTop: `1px solid ${C.brassSoft}` }}>
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <SectionHead eyebrow={aboutKatrina6.eyebrow} headline={aboutKatrina6.title} intro={aboutKatrina6.lede} />

          <div className="grid lg:grid-cols-12 gap-5" data-testid="katrina-bio">
            <div className="lg:col-span-4">
              <div className="max-w-[320px]">
                <FounderPortrait />
              </div>
              <a
                href={brand6.linkedin}
                target="_blank"
                rel="noreferrer"
                data-testid="katrina-linkedin"
                className="mt-6 inline-flex items-center gap-2.5 font-inter text-[11px] uppercase font-semibold transition-colors"
                style={{ color: C.accentFunctional, letterSpacing: "0.22em" }}
              >
                <LinkedinLogo size={15} weight="regular" />
                Connect on LinkedIn
              </a>
              <dl className="mt-8">
                {aboutKatrina6.meta.map(([k, v]) => (
                  <div key={k} className="py-3 flex items-baseline justify-between gap-4" style={{ borderTop: `1px solid ${C.paperOver}` }}>
                    <dt className="font-inter text-[10px] uppercase font-semibold" style={{ color: C.accentMetal, letterSpacing: "0.26em" }}>{k}</dt>
                    <dd className="font-inter text-[13px] text-right" style={{ color: C.inkOnLight }}>{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <Box6 tone="white" radius="card" className="lg:col-span-8 p-9 lg:p-11 space-y-6" data-testid="katrina-bio-box">
              <BoxLabel>Biography</BoxLabel>
              {aboutKatrina6.bio.map((p, i) => (
                <p key={i} className="font-inter leading-[1.8]" style={{ color: C.inkOnLight, fontSize: "1.02rem" }}>
                  {p}
                </p>
              ))}
            </Box6>
          </div>

          <div className="mt-14">
            <Eyebrow6 className="mb-8">Credentials & authority</Eyebrow6>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="katrina-credentials">
              {aboutKatrina6.credentials.map(([label, note], i) => (
                <Box6 key={label} tone="white" radius="resource" className="p-7" data-testid={`credential-${i}`}>
                  <p className="font-inter text-[10.5px] uppercase font-semibold mb-2" style={{ color: C.accentMetal, letterSpacing: "0.2em" }}>
                    {label}
                  </p>
                  <p className="font-inter" style={{ color: C.inkOnLight, fontSize: "0.92rem" }}>
                    {note}
                  </p>
                </Box6>
              ))}
            </div>
          </div>

          <div className="mt-14">
            <Eyebrow6 className="mb-8">{aboutKatrina6.approachEyebrow}</Eyebrow6>
            <div className="grid md:grid-cols-3 gap-5" data-testid="katrina-approach">
              {aboutKatrina6.approach.map((a, i) => (
                <Box6 key={a.label} tone="blue" radius="resource" className="p-7" data-testid={`approach-${i}`}>
                  <h3 className="font-cormorant" style={{ color: C.textOnDark, fontSize: "1.3rem", fontWeight: 400, lineHeight: 1.2 }}>
                    {a.label}
                  </h3>
                  <p className="mt-3 font-inter leading-[1.65]" style={{ color: C.whiteOnDark, fontSize: "0.93rem" }}>
                    {a.body}
                  </p>
                </Box6>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3 — Speaking & Keynotes (one-pager) */}
      <section id="speaking" data-testid="speaking-section" style={{ background: C.surfacePrimary, color: C.textOnDark }}>
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-24 lg:py-28">
          <SectionHead eyebrow={sp.eyebrow} headline={sp.headline} intro={sp.intro} dark />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5" data-testid="speaking-topics">
            {sp.topics.map(([title, dek], i) => (
              <Box6 as="article" key={title} tone="glass" radius="resource" className="p-7" data-testid={`speaking-topic-${i}`}>
                <Pill6 tone="light">{`Topic ${String(i + 1).padStart(2, "0")}`}</Pill6>
                <h3 className="mt-5 font-cormorant" style={{ color: C.textOnDark, fontSize: "1.3rem", fontWeight: 400, lineHeight: 1.2 }}>
                  {title}
                </h3>
                <p className="mt-3 font-inter leading-[1.6]" style={{ color: C.whiteOnDark, fontSize: "0.9rem" }}>
                  {dek}
                </p>
              </Box6>
            ))}
            <Box6 tone="ink" radius="resource" className="p-7 flex flex-col" data-testid="speaking-booking">
              <BoxLabel dark>{sp.booking.label}</BoxLabel>
              <p className="font-inter leading-[1.6] grow" style={{ color: C.whiteOnDark, fontSize: "0.92rem" }}>
                {sp.booking.body}
              </p>
              <div className="mt-6">
                <Cta6 to={sp.cta.href} variant="light" testId="speaking-inquiry-cta" className="w-full justify-between">
                  {sp.cta.label}
                </Cta6>
              </div>
            </Box6>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              ["Audiences", sp.audiences, "speaking-audiences"],
              ["Formats", sp.formats, "speaking-formats"],
              ["Authority signals", sp.signals, "speaking-signals"],
            ].map(([title, items, tid]) => (
              <Box6 key={title} tone="glass" radius="resource" className="p-7" data-testid={tid}>
                <BoxLabel dark>{title}</BoxLabel>
                <ul>
                  {items.map((item) => (
                    <li key={item} className="font-inter leading-[1.5] py-2.5" style={{ color: C.whiteOnDark, fontSize: "0.92rem", borderBottom: `1px solid ${C.brassSoft}` }}>
                      {item}
                    </li>
                  ))}
                </ul>
              </Box6>
            ))}
          </div>
        </div>
      </section>

      <CtaBand6 tone="ink" eyebrow="Ready to begin" headline="Every inquiry is reviewed personally." testId="about-cta" />
      <SiteFooter6 />
    </main>
  );
}
